import { detectOverflow, type State } from '@popperjs/core';

import { quantitativeSizeToString } from '../../../util/sizes';
import * as types from '../types';

import { ModifierChild, ModifierConfiguration } from './base';

/**
 * Returns the child element, {@link ChildNode}, of the popper element to which the max-height
 * constraint should be applied when the {@link ConstrainMaxHeightToViewport} modifier is used.
 *
 * This child element is typically the {@link Paper} component that is defined either by the
 * 'PaperComponent' prop on the {@link Popper} or by default in the {@link Popper} itself (by
 * MUI).  However, it does not necessarily need to be the case that this element is a
 * {@link Paper} element, or an element that has the 'MuiPaper-root' class, so this function
 * generically returns the first child of the popper element.
 */
const getPopperElementToConstrain = (state: State) => state.elements.popper.firstChild;

const ConstrainMaxHeightToViewportDefaultOptions = {
  listeners: ['resize'] as types.PopperResizeListener[],
  offset: 4,
  padding: 10,
} as const satisfies types.CustomModifierOptions<
  typeof types.CustomModifierName.ConstrainMaxHeightToViewport
>;

/**
 * A {@link ModifierConfiguration} for a custom {@link types.CustomModifier} that ensures that
 * the height of the {@link Popover} does not exceed the height of the available space between
 * the bottom of the target element and the bottom of the viewport (accounting for any padding
 * or offset specified in the options).
 *
 * If the 'maxHeight' prop is provided to the {@link Popper}, that value will be used as an
 * upper bound on the max height of the {@link Popover}'s content, ensuring that the content
 * does not exceed this value even if there is more available space in the viewport, but will
 * still not exceed the available space in the viewport when the screen shrinks vertically.
 *
 * When the 'maxHeight' prop is not provided, the {@link Popover}'s content will automatically
 * expand to fill the available space between the bottom of the target element and the bottom
 * of the viewport (accounting for any padding or offset specified in the options).
 */
export class ConstrainMaxHeightToViewport extends ModifierConfiguration<
  typeof types.CustomModifierName.ConstrainMaxHeightToViewport,
  typeof ConstrainMaxHeightToViewportDefaultOptions
> {
  defaultOptions = ConstrainMaxHeightToViewportDefaultOptions;
  name = types.CustomModifierName.ConstrainMaxHeightToViewport;

  protected children(
    options: types.WithDefaults<
      types.CustomModifierOptions<typeof types.CustomModifierName.ConstrainMaxHeightToViewport>,
      typeof ConstrainMaxHeightToViewportDefaultOptions
    >,
    props: types.NullableButRequiredCustomModifierApplicableProps,
  ): ModifierChild[] {
    return [
      ModifierChild.create('eventListeners', this, {
        options: {
          resize: options.listeners.includes('resize'),
          scroll: options.listeners.includes('scroll'),
        },
        settings: {
          conflictResolution: types.ModifierConflictResolution.Overwrite,
          messageOnConflict:
            'The eventListeners modifier must only listen to the events specified in the ' +
            'listeners option when this custom modifier is used.',
        },
      }),
      ModifierChild.create('flip', this, {
        options: {
          allowedAutoPlacements: ['bottom'],
          fallbackPlacements: [],
        },
        settings: {
          conflictResolution: types.ModifierConflictResolution.Overwrite,
          messageOnConflict:
            'The flip modifier must only allow placement at the bottom when this custom ' +
            'modifier is used.',
        },
      }),
      ModifierChild.create('preventOverflow', this, {
        options: { boundary: 'viewport' },
        settings: {
          conflictResolution: types.ModifierConflictResolution.Overwrite,
          messageOnConflict:
            'The preventOverflow modifier must use the viewport as its boundary when this ' +
            'custom modifier is used.',
        },
      }),
      ModifierChild.create('customSize', this, {
        /**
         * Applies the custom modifier logic responsible for ensuring that the {@link Popper}
         * does not exceed both the prop-provided 'maxHeight' as well as the available space
         * between the bottom of the target element and the bottom of the viewport.
         */
        fn({ state }) {
          const constrainableElement = getPopperElementToConstrain(state);
          if (constrainableElement instanceof HTMLElement) {
            const height = state.rects.popper.height;

            const withRespectToPropMaxHeight = (overflowMaxHeight: number): string => {
              const overflowed = quantitativeSizeToString(Math.max(overflowMaxHeight, 10), 'px');

              if (props.maxHeight === null) {
                return quantitativeSizeToString(overflowMaxHeight, 'px');
              }
              return (
                'min(' +
                [quantitativeSizeToString(props.maxHeight, 'px'), overflowed].join(', ') +
                ')'
              );
            };

            const { bottom } = detectOverflow(state);
            /* Note: The 'bottom' value can be positive or negative, depending on whether or not
               the Popper's height exceeds the available space in the viewport or does not exceed
               the available space. */
            if (bottom !== 0) {
              constrainableElement.style.maxHeight = withRespectToPropMaxHeight(
                height - bottom - options.padding + options.offset,
              );
            }
          }
        },
        phase: 'main',
        settings: {},
      }),
    ];
  }

  public logPropCollisions(props: types.NullableButRequiredCustomModifierApplicableProps): void {
    if (props.offset !== null) {
      this.logWarning(
        'The "offset" prop is ignored when using this modifier. Intstead of providing as a ' +
          "prop, consider configuring this modifier with an 'offset' option.",
      );
    }
  }
}
